<script setup>
import { computed, ref, watch } from "vue";
import { usePermissionStore } from "@/stores/usePermissionStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  permissionId: { type: Number, required: true },
});

const emit = defineEmits(["close"]);

const permissionStore = usePermissionStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

const permission = computed(() => {
  if (
    permissionStore.selectedPermission &&
    permissionStore.selectedPermission.permissionId === props.permissionId
  ) {
    return permissionStore.selectedPermission;
  }
  return (
    permissionStore.permissions.find((p) => p.permissionId === props.permissionId) ||
    null
  );
});

watch(
  () => props.permissionId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    try {
      await permissionStore.getPermissionById(newId);
    } catch (e) {
      console.error("Erro ao carregar permissão no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div
      ref="panelRef"
      class="permission-card-modal-content"
      :style="panelStyle"
    >
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes da Permissão</h3>
        <button
          @click="emit('close')"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="permission && !isLoadingData" class="card-content-grid">
        <div class="details-col">
          <h3 class="text-title-bold">
            {{ toTitleCase(permission.permissionName) }}
            <span class="title-id">(Id: {{ permission.permissionId }})</span>
          </h3>

          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">Tabela Referenciada</span>
              <span class="meta-value">
                {{ permission.referencedTable || "Global" }}
              </span>
            </div>

            <div class="meta-item meta-item-full meta-item-spaced">
              <span class="meta-label">Descrição</span>
              <span class="meta-value description-text">
                {{ permission.description || "Nenhuma descrição informada." }}
              </span>
            </div>
          </div>

          <hr class="content-separator" />

          <div class="footer-info">
            <div class="users-row">
              <div class="user-chip">
                <div class="chip-text">
                  <span class="chip-label">Criação</span>
                  <span class="chip-date">{{
                    formatDateTimeForDisplay(permission.createdAt)
                  }}</span>
                </div>
              </div>

              <div class="user-chip">
                <div class="chip-text">
                  <span class="chip-label">Última Atualização</span>
                  <span class="chip-date">{{
                    formatDateTimeForDisplay(permission.updatedAt)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes da permissão...
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

.permission-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 650px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

/* Os botões do cabeçalho continuam sendo clique, não alça */
.modal-header--draggable .modal-close-btn,
.modal-header--draggable .modal-header-actions button {
  cursor: pointer;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.card-content-grid {
  /* body padding mais contido */
  padding: 1rem;
}

.details-col {
  width: 100%;
}

/* título no “peso” do TextCardModal */
.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 6px 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

/* dá um “respiro” entre o campo de tabela e a descrição */
.meta-item-spaced {
  margin-top: 6px;
}

.footer-info {
  padding-top: 8px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 8px;
}

</style>
